import type { Difficulty, ExamId } from "../data/types";
import { ArrowLeft, ClipboardList, PlayCircle, ExternalLink, FileText, Library, Target, Timer } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { EXAM_BY_ID, isExamId, PASS_SCORE } from "../data/exams";
import { ExamBadge, ProgressBar, ScoreRing, useDocumentTitle } from "../components/ui";
import { formatDateTime } from "../lib/dates";
import {
  buildFixedSession,
  buildMock, buildPractice, mockDurationMs, selectMockQuestions, selectPracticeQuestions, sessionStore,
  type PracticeFilter, type PracticeType,
} from "../lib/session";
import { domainMastery, examMastery, readiness } from "../lib/stats";
import { progressStore, useProgress } from "../lib/store";
import { toggleTaskCheck } from "../lib/progress";
import { pick, useT } from "../i18n";
import { NotFoundPage } from "./NotFoundPage";
import { LoginCard } from "../components/Auth";
import { bankStats, questionsForExam } from "../data/questions";
import { useAuth } from "../lib/auth";

export const TRIAL_SIZE = 10;

/** Khách: làm thử bộ câu công khai của đề (không cần đăng nhập), rồi mời đăng nhập để làm tiếp. */
function TrialCard({ examId }: { examId: ExamId }) {
  const t = useT();
  const navigate = useNavigate();
  const qs = questionsForExam(examId).slice(0, TRIAL_SIZE);
  const total = bankStats().byExam[examId] ?? 0;
  const code = EXAM_BY_ID[examId].code;
  const start = () => {
    sessionStore.set({ ...buildFixedSession("practice", { vi: `Làm thử ${code}`, en: `${code} free trial` }, qs), examId, trial: true });
    navigate("/session");
  };
  return (
    <div className="grid grid-2 mt">
      <div className="card">
        <h3><PlayCircle className="ico" size={20} aria-hidden="true" />{t(`Làm thử ${qs.length} câu miễn phí`, `Try ${qs.length} questions free`)}</h3>
        <p className="small muted">
          {t(
            `Không cần đăng nhập. Đề tiếng Anh như thi thật, xem đáp án và giải thích tiếng Việt sau mỗi câu. Ngân hàng đầy đủ có ${total} câu cho ${code}.`,
            `No sign-in needed. English questions like the real exam, with answers and explanations after each one. The full bank has ${total} questions for ${code}.`,
          )}
        </p>
        <button type="button" className="btn primary" onClick={start} disabled={!qs.length}>{t("Bắt đầu làm thử", "Start free trial")}</button>
      </div>
      <LoginCard reason={t(
        `Đăng nhập Google để mở toàn bộ ${total} câu, thi thử bấm giờ, luyện theo domain và lưu tiến độ.`,
        `Sign in with Google to unlock all ${total} questions, timed mock exams, domain practice and saved progress.`,
      )} />
    </div>
  );
}

const FILTERS: { id: PracticeFilter; vi: string; en: string }[] = [
  { id: "all", vi: "Tất cả", en: "All" },
  { id: "unseen", vi: "Chưa làm", en: "Unseen" },
  { id: "wrong", vi: "Đang sai", en: "Wrong" },
  { id: "bookmarked", vi: "Đã đánh dấu", en: "Bookmarked" },
  { id: "official", vi: "Câu mẫu chính thức", en: "Official samples" },
];
const COUNTS = [5, 10, 20, 30, 60, 999];
const DIFFS: { id: Difficulty; vi: string; en: string }[] = [
  { id: 1, vi: "Cơ bản", en: "Basic" },
  { id: 2, vi: "Trung bình", en: "Intermediate" },
  { id: 3, vi: "Nâng cao", en: "Advanced" },
];
const TYPES: { id: PracticeType; vi: string; en: string }[] = [
  { id: "any", vi: "Tất cả", en: "All" },
  { id: "single", vi: "Một đáp án", en: "Single answer" },
  { id: "multi", vi: "Nhiều đáp án", en: "Multiple answers" },
];

export function ExamPage() {
  const { examId } = useParams();
  const p = useProgress();
  const t = useT();
  const lang = p.lang;
  const navigate = useNavigate();
  const [domains, setDomains] = useState<string[]>([]);
  const [count, setCount] = useState(10);
  const [filter, setFilter] = useState<PracticeFilter>("all");
  const [difficulties, setDifficulties] = useState<Difficulty[]>([]);
  const [qType, setQType] = useState<PracticeType>("any");
  const [examMode, setExamMode] = useState(false);
  const authed = useAuth().status === "authed";
  const exam = isExamId(examId) ? EXAM_BY_ID[examId] : undefined;
  useDocumentTitle(exam ? exam.code : t("Không tìm thấy", "Not found"));

  const available = useMemo(
    () => (exam ? selectPracticeQuestions(exam.id, { domains, count: 9999, filter, difficulties, type: qType }, p, 1).length : 0),
    [exam, domains, filter, difficulties, qType, p],
  );

  if (!exam) return <NotFoundPage />;
  const m = examMastery(exam.id, p);
  const r = readiness(exam.id, p);
  const mockLen = selectMockQuestions(exam.id, 1).length;
  const mockMin = Math.round(mockDurationMs(exam.id, mockLen) / 60000);
  const attempts = p.attempts.filter((a) => a.examId === exam.id && a.kind === "mock");
  const tasksDone = exam.taskStatements?.filter((ts) => p.taskChecks[`${exam.id}:${ts.id}`]).length ?? 0;

  const toggleDomain = (id: string) =>
    setDomains((d) => (d.includes(id) ? d.filter((x) => x !== id) : [...d, id]));

  const startPractice = (onlyDomain?: string) => {
    const s = buildPractice(
      exam.id,
      onlyDomain ? { domains: [onlyDomain], count, filter: "all" } : { domains, count, filter, difficulties, type: qType, examMode },
      p,
    );
    if (!s) return;
    sessionStore.set(s);
    navigate("/session");
  };

  const startMock = () => {
    if (sessionStore.get() && !window.confirm(t("Bạn đang có một phiên làm dở. Bắt đầu bài thi mới sẽ hủy phiên đó. Tiếp tục?", "You have a session in progress. Starting a new exam will discard it. Continue?"))) return;
    sessionStore.set(buildMock(exam.id));
    navigate("/session");
  };

  const facts: [string, string][] = [
    [t("Số câu", "Items"), String(exam.items)],
    [t("Thời gian", "Time limit"), t(`${exam.durationMin} phút`, `${exam.durationMin} minutes`)],
    [t("Dạng câu", "Item format"), t("1 hoặc nhiều đáp án (đề ghi rõ số cần chọn)", "Multiple choice & multiple response (each states how many)")],
    ...(exam.structure ? [[t("Cấu trúc", "Structure"), pick(exam.structure, lang)] as [string, string]] : []),
    [t("Điểm đạt", "Passing score"), t(`${PASS_SCORE} trên thang 100–1000`, `${PASS_SCORE} on a 100–1000 scale`)],
    [t("Lệ phí", "Fee"), `$${exam.priceUsd} USD`],
    [t("Ngôn ngữ đề", "Exam languages"), t("Tiếng Anh, tiếng Nhật", "English, Japanese")],
    [t("Hiệu lực", "Validity"), t("12 tháng · gia hạn miễn phí nếu đúng hạn", "12 months · free on-time renewal")],
  ];

  return (
    <div className="exam-detail-page">
      <p className="small"><Link to="/exams"><ArrowLeft className="ico" size={16} aria-hidden="true" /> {t("Tất cả đề thi", "All exams")}</Link></p>
      <div className="row" style={{ alignItems: "flex-start" }}>
        <ExamBadge examId={exam.id} size={52} />
        <div style={{ flex: 1, minWidth: 240 }}>
          <h1 className="mb-0">{exam.code}</h1>
          <div style={{ fontWeight: 600 }}>{exam.name}</div>
          <p className="muted small mb-0">{pick(exam.tagline, lang)}</p>
          <p className="muted small">{pick(exam.audience, lang)}</p>
        </div>
      </div>

      <nav className="exam-jump-nav" aria-label={t("Đi tới phần", "Jump to section")}>
        <a href="#exam-actions">{t("Bắt đầu", "Get started")}</a>
        <a href="#exam-details">{t("Thông tin kỳ thi", "Exam details")}</a>
        {authed && <a href="#exam-practice">{t("Luyện tập", "Practice")}</a>}
        <a href="#exam-blueprint">{t("Nội dung thi", "Exam content")}</a>
        <a href="#exam-resources">{t("Tài liệu & lịch sử", "Resources & history")}</a>
      </nav>

      <div id="exam-actions">
      {authed ? (
      <div className="grid grid-2 mt">
        <div className="card row" style={{ gap: "1.25rem" }}>
          <ScoreRing percent={r === null ? 0 : (r / 1000) * 100} color={r !== null && r >= PASS_SCORE ? "var(--ok)" : undefined}>
            <div>
              <div className="big">{r ?? "—"}</div>
              <div className="tiny muted">{t("điểm dự đoán", "predicted")}</div>
            </div>
          </ScoreRing>
          <div style={{ flex: 1, minWidth: 160 }}>
            <p className="small mb-0">{t("Đã luyện", "Practiced")} <strong>{m.seen}/{m.total}</strong> · {t("thành thạo", "mastered")} <strong>{m.mastered}</strong></p>
            <p className="small">{t("Độ chính xác gần nhất", "Recent accuracy")}: <strong>{Math.round(m.accuracy * 100)}%</strong></p>
            <p className="tiny muted mb-0">
              {t(
                `Điểm dự đoán = độ chính xác theo trọng số domain chính thức × độ phủ ngân hàng câu. Cần ≥ ${PASS_SCORE} để đạt.`,
                `Prediction = accuracy weighted by the official domain blueprint × question-bank coverage. ${PASS_SCORE}+ to pass.`,
              )}
            </p>
          </div>
        </div>
        <div className="card">
          <h3><Timer className="ico" size={20} aria-hidden="true" />{t("Thi thử", "Mock exam")}</h3>
          <p className="small muted">
            {t(
              `${mockLen} câu · ${mockMin} phút — cùng nhịp đề thật (${exam.items} câu / ${exam.durationMin} phút), chia câu theo tỷ trọng domain.`,
              `${mockLen} questions · ${mockMin} minutes — the real exam's pace (${exam.items} items / ${exam.durationMin} min), weighted by domain.`,
            )}
            {exam.scenarios && " " + t(`Rút ngẫu nhiên ${exam.scenariosPerMock} trong ${exam.scenarios.length} kịch bản như đề thật.`, `${exam.scenariosPerMock} of ${exam.scenarios.length} scenarios drawn at random, like the real exam.`)}
            {" "}{t("Đề bằng tiếng Anh; giải thích hiện sau khi nộp bài.", "Questions in English; explanations appear after you submit.")}
          </p>
          <button type="button" className="btn primary" onClick={startMock}>{t("Bắt đầu thi thử", "Start mock exam")}</button>
          {attempts[0] && (
            <p className="tiny muted mt mb-0">
              {t("Lần gần nhất", "Latest")}: <Link to={`/result/${attempts[0].id}`}>{attempts[0].scaled}/1000 · {attempts[0].passed ? t("ĐẠT", "PASS") : t("chưa đạt", "not passed")}</Link>
              {" "}· {t("tốt nhất", "best")} {Math.max(...attempts.map((a) => a.scaled))}
            </p>
          )}
        </div>
      </div>
      ) : (
        <TrialCard examId={exam.id} />
      )}
      </div>

      <section id="exam-details">
      <div className="section-title exam-section-heading"><h2><ClipboardList className="ico" size={22} aria-hidden="true" />{t("Thông tin kỳ thi chính thức", "Official exam details")}</h2></div>
      <div className="card">
        <dl className="facts">
          {facts.map(([k, v]) => (
            <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
          ))}
        </dl>
        <div className="row mt">
          <a className="btn sm" href={exam.guideUrl} target="_blank" rel="noreferrer noopener"><FileText className="ico" size={16} aria-hidden="true" /> {t("Exam guide (PDF chính thức)", "Exam guide (official PDF)")} <ExternalLink className="ico" size={14} aria-hidden="true" /></a>
          <Link className="btn sm ghost" to="/guide">{t("Chính sách thi, thi lại, gia hạn", "Policies, retakes, renewal")}</Link>
        </div>
      </div>
      </section>

      <div className="section-title exam-section-heading" id="exam-blueprint"><h2>{t("Blueprint: domain & mức thành thạo", "Blueprint: domains & mastery")}</h2></div>
      <div className="card">
        {[...exam.domains].sort((a, b) => b.weight - a.weight).map((d) => {
          const dm = domainMastery(exam.id, d.id, p);
          return (
            <div key={d.id} className="domain-row">
              <div>
                <div style={{ fontWeight: 600 }}>{d.name} <span className="chip tiny">{d.weight}%</span></div>
                <div className="tiny muted">{pick(d.summary, lang)}</div>
              </div>
              <ProgressBar value={dm.total ? ((dm.seen ? dm.accuracy : 0) * dm.seen / dm.total) * 100 : 0} label={`${t("Thành thạo", "Mastery")} ${d.name}`} tone={dm.accuracy >= 0.7 && dm.seen === dm.total ? "ok" : undefined} />
              {authed ? (
                <button type="button" className="btn sm" onClick={() => startPractice(d.id)} disabled={!dm.total}>
                  {t("Luyện", "Practice")} ({dm.total})
                </button>
              ) : null}
            </div>
          );
        })}
        <p className="tiny muted mt mb-0">{t(`Tỷ trọng theo exam guide chính thức (${"v1.0, 07/2026"}).`, "Weights from the official exam guide (v1.0, July 2026).")}</p>
      </div>

      {exam.taskStatements && (
        <>
          <div className="section-title">
            <h2>{t("Task statements — tự đánh giá", "Task statements — self-assessment")}</h2>
            <span className="small muted">{tasksDone}/{exam.taskStatements.length} {t("đã nắm vững", "confident")}</span>
          </div>
          <div className="card">
            <ProgressBar value={(tasksDone / exam.taskStatements.length) * 100} label={t("Tiến độ task statements", "Task statement progress")} tone={tasksDone === exam.taskStatements.length ? "ok" : undefined} />
            {exam.domains.map((d) => {
              const items = exam.taskStatements!.filter((ts) => ts.domain === d.id);
              if (!items.length) return null;
              return (
                <div key={d.id} className="mt">
                  <h3 className="small mb-0">{d.name}</h3>
                  <ul className="task-list">
                    {items.map((ts) => {
                      const key = `${exam.id}:${ts.id}`;
                      return (
                        <li key={ts.id}>
                          <label>
                            <input type="checkbox" checked={!!p.taskChecks[key]} onChange={() => progressStore.update((x) => toggleTaskCheck(x, key, Date.now()))} />
                            <span>
                              <strong lang="en">{ts.id} {ts.title}</strong>
                              {lang === "vi" && <span className="tiny muted d-block">{ts.vi}</span>}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </>
      )}

      {authed && (
        <>
      <div className="section-title exam-section-heading" id="exam-practice"><h2><Target className="ico" size={22} aria-hidden="true" />{t("Luyện tập tùy chỉnh", "Custom practice")}</h2></div>
      <div className="card stack">
        <div className="field">
          <span className="label">{t("Domain (bỏ trống = tất cả)", "Domains (none = all)")}</span>
          <div className="pill-group">
            {exam.domains.map((d) => (
              <button key={d.id} type="button" className="pill" aria-pressed={domains.includes(d.id)} onClick={() => toggleDomain(d.id)}>{d.name}</button>
            ))}
          </div>
        </div>
        <div className="field">
          <span className="label">{t("Bộ lọc", "Filter")}</span>
          <div className="pill-group">
            {FILTERS.map((f) => (
              <button key={f.id} type="button" className="pill" aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>{t(f.vi, f.en)}</button>
            ))}
          </div>
        </div>
        <details className="exam-advanced-filters">
          <summary>{t("Bộ lọc nâng cao", "Advanced filters")}</summary>
          <div className="grid grid-2 mt">
            <div className="field">
              <span className="label">{t("Độ khó (bỏ trống = tất cả)", "Difficulty (none = all)")}</span>
              <div className="pill-group">
                {DIFFS.map((d) => (
                  <button key={d.id} type="button" className="pill" aria-pressed={difficulties.includes(d.id)}
                    onClick={() => setDifficulties((x) => (x.includes(d.id) ? x.filter((v) => v !== d.id) : [...x, d.id]))}>{t(d.vi, d.en)}</button>
                ))}
              </div>
            </div>
            <div className="field">
              <span className="label">{t("Loại câu", "Question type")}</span>
              <div className="pill-group">
                {TYPES.map((x) => (
                  <button key={x.id} type="button" className="pill" aria-pressed={qType === x.id} onClick={() => setQType(x.id)}>{t(x.vi, x.en)}</button>
                ))}
              </div>
            </div>
          </div>
        </details>
        <div className="field">
          <span className="label">{t("Chế độ", "Mode")}</span>
          <div className="pill-group">
            <button type="button" className="pill" aria-pressed={!examMode} onClick={() => setExamMode(false)}>{t("Học — xem đáp án từng câu", "Study — see answers as you go")}</button>
            <button type="button" className="pill" aria-pressed={examMode} onClick={() => setExamMode(true)}>{t("Thi — bấm giờ, chấm khi nộp", "Exam — timed, graded at the end")}</button>
          </div>
        </div>
        <div className="field">
          <span className="label">{t("Số câu", "Questions")}</span>
          <div className="pill-group">
            {COUNTS.map((c) => (
              <button key={c} type="button" className="pill" aria-pressed={count === c} onClick={() => setCount(c)}>{c === 999 ? t("Tất cả", "All") : c}</button>
            ))}
          </div>
        </div>
        <div className="row">
          <button type="button" className="btn primary" disabled={!available} onClick={() => startPractice()}>
            {t("Bắt đầu luyện", "Start practice")} ({Math.min(available, count)} {t("câu", "questions")}
            {examMode ? ` · ${Math.round(mockDurationMs(exam.id, Math.min(available, count)) / 60000)} ${t("phút", "min")}` : ""})
          </button>
          {!available && <span className="small muted">{t("Không có câu nào khớp bộ lọc.", "No questions match this filter.")}</span>}
        </div>
      </div>
        </>
      )}

      {exam.scenarios && (
        <>
      <div className="section-title exam-section-heading" id="exam-scenarios"><h2>{t("Kịch bản trong đề", "Exam scenarios")}</h2></div>
          <div className="grid grid-2">
            {exam.scenarios.map((s, i) => (
              <div key={s.id} className="card flat">
                <h3>{i + 1}. {pick(s.title, lang)}</h3>
                <p className="small muted">{pick(s.context, lang)}</p>
                {s.primaryDomains && (
                  <div className="row tiny">
                    {s.primaryDomains.map((id) => <span key={id} className="chip tiny">{exam.domains.find((d) => d.id === id)?.name}</span>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      <div className="section-title exam-section-heading" id="exam-resources"><h2><Library className="ico" size={22} aria-hidden="true" />{t("Tài liệu chính thức nên học", "Recommended official resources")}</h2></div>
      <ul>
        {exam.resources.map((res) => (
          <li key={res.url + res.title.en}><a href={res.url} target="_blank" rel="noreferrer noopener">{pick(res.title, lang)}</a></li>
        ))}
      </ul>

      {attempts.length > 0 && (
        <>
          <div className="section-title exam-section-heading" id="exam-history"><h2>{t("Lịch sử thi thử", "Mock exam history")}</h2></div>
          <div className="card table-wrap">
            <table className="history">
              <thead><tr><th>{t("Thời gian", "Date")}</th><th>{t("Điểm", "Score")}</th><th>{t("Đúng", "Correct")}</th><th>{t("Kết quả", "Result")}</th></tr></thead>
              <tbody>
                {attempts.map((a) => (
                  <tr key={a.id}>
                    <td><Link to={`/result/${a.id}`}>{formatDateTime(a.finishedAt, lang)}</Link></td>
                    <td>{a.scaled}</td>
                    <td>{a.correct}/{a.total}</td>
                    <td><span className={`chip ${a.passed ? "ok" : "bad"}`}>{a.passed ? t("Đạt", "Pass") : t("Chưa đạt", "Not passed")}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
